import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { finalize } from 'rxjs/operators';
import { CreateWorkflowAssistantSuggestion, WorkflowAssistantSubmissionType, WorkflowAssistantSuggestion, WorkflowAssistantSuggestionStatus } from './workflow-assistant.models';
import { WorkflowAssistantSuggestionService } from './workflow-assistant-suggestion.service';
import { richTextRequired, WORKFLOW_ASSISTANT_QUILL_CONFIG, WORKFLOW_ASSISTANT_RICH_TEXT_MAX_LENGTH } from './workflow-assistant-quill';

@Component({
  selector: 'app-workflow-assistant-suggestions-dialog',
  templateUrl: './workflow-assistant-suggestions-dialog.component.html',
  styleUrls: ['./workflow-assistant-suggestions-dialog.component.scss'],
})
export class WorkflowAssistantSuggestionsDialogComponent implements OnInit {
  readonly quillConfig = WORKFLOW_ASSISTANT_QUILL_CONFIG;
  readonly statuses = [
    { value: WorkflowAssistantSuggestionStatus.New, label: 'New' },
    { value: WorkflowAssistantSuggestionStatus.UnderReview, label: 'Under review' },
    { value: WorkflowAssistantSuggestionStatus.Accepted, label: 'Accepted' },
    { value: WorkflowAssistantSuggestionStatus.Implemented, label: 'Implemented' },
    { value: WorkflowAssistantSuggestionStatus.Rejected, label: 'Rejected' },
  ];
  readonly suggestionForm: FormGroup;
  suggestions: WorkflowAssistantSuggestion[] = [];
  loading = false;
  submitting = false;
  savingID?: number;
  message = '';
  error = '';

  constructor(
    formBuilder: FormBuilder,
    private suggestionService: WorkflowAssistantSuggestionService,
    public dialogRef: MatDialogRef<WorkflowAssistantSuggestionsDialogComponent>,
  ) {
    this.suggestionForm = formBuilder.group({
      question: ['', [Validators.required, Validators.maxLength(500)]],
      expectedBehavior: ['', [richTextRequired(), Validators.maxLength(WORKFLOW_ASSISTANT_RICH_TEXT_MAX_LENGTH)]],
      exampleReference: ['', Validators.maxLength(300)],
    });
  }

  /**
   * Loads the current review queue when the dialog opens.
   */
  ngOnInit(): void {
    this.loadSuggestions();
  }

  /**
   * Refreshes suggestions from the server and surfaces authorization or network failures locally.
   */
  loadSuggestions(): void {
    this.loading = true;
    this.error = '';
    this.suggestionService.getAll().pipe(finalize(() => this.loading = false)).subscribe({
      next: suggestions => this.suggestions = suggestions,
      error: () => this.error = 'The feedback queue could not be loaded.',
    });
  }

  /**
   * Validates and submits the proposed question under the current SA account.
   */
  submitSuggestion(): void {
    if (this.suggestionForm.invalid) {
      this.suggestionForm.markAllAsTouched();
      return;
    }

    const value = this.suggestionForm.value;
    const suggestion: CreateWorkflowAssistantSuggestion = {
      question: value.question.trim(),
      expectedBehavior: value.expectedBehavior.trim(),
      exampleReference: value.exampleReference?.trim() || undefined,
    };
    this.submitting = true;
    this.error = '';
    this.message = '';
    this.suggestionService.create(suggestion).pipe(finalize(() => this.submitting = false)).subscribe({
      next: created => {
        this.suggestions = [created, ...this.suggestions];
        this.suggestionForm.reset();
        this.message = 'Suggestion submitted.';
      },
      error: () => this.error = 'The suggestion could not be submitted.',
    });
  }

  /**
   * Persists the selected triage state and notes for one suggestion.
   * @param suggestion Queue item edited by the reviewer.
   */
  saveSuggestion(suggestion: WorkflowAssistantSuggestion): void {
    this.savingID = suggestion.id;
    this.error = '';
    this.message = '';
    this.suggestionService.update(suggestion.id, {
      status: suggestion.status,
      developerNotes: suggestion.developerNotes?.trim() || undefined,
    }).pipe(finalize(() => this.savingID = undefined)).subscribe({
      next: updated => {
        const index = this.suggestions.findIndex(item => item.id === updated.id);
        if (index >= 0) this.suggestions[index] = updated;
        this.message = `Queue item #${updated.id} saved.`;
      },
      error: () => this.error = `Queue item #${suggestion.id} could not be saved.`,
    });
  }

  /**
   * Downloads actionable suggestions in a format developers can feed into their normal workflow.
   * @param format Export representation requested by the reviewer.
   */
  exportSuggestions(format: 'markdown' | 'json'): void {
    const suggestions = this.getActionableSuggestions();
    const content = format === 'json'
      ? JSON.stringify(suggestions, null, 2)
      : this.createMarkdown(suggestions);
    const mimeType = format === 'json' ? 'application/json' : 'text/markdown';
    const extension = format === 'json' ? 'json' : 'md';
    const anchor = document.createElement('a');
    anchor.href = URL.createObjectURL(new Blob([content], { type: `${mimeType};charset=utf-8` }));
    anchor.download = `workflow-assistant-feedback.${extension}`;
    anchor.click();
    URL.revokeObjectURL(anchor.href);
  }

  /**
   * Copies actionable suggestions as Markdown for direct use in a developer task.
   */
  async copySuggestions(): Promise<void> {
    try {
      await navigator.clipboard.writeText(this.createMarkdown(this.getActionableSuggestions()));
      this.message = 'Actionable feedback copied.';
      this.error = '';
    } catch {
      this.error = 'Feedback could not be copied.';
    }
  }

  /**
   * Returns work that still needs review or implementation, excluding completed decisions.
   */
  private getActionableSuggestions(): WorkflowAssistantSuggestion[] {
    return this.suggestions.filter(suggestion =>
      suggestion.status !== WorkflowAssistantSuggestionStatus.Implemented
      && suggestion.status !== WorkflowAssistantSuggestionStatus.Rejected);
  }

  /**
   * Distinguishes captured incorrect answers from proposed assistant questions in the shared queue.
   * @param suggestion Queue item to classify.
   */
  isIncorrectAnswer(suggestion: WorkflowAssistantSuggestion): boolean {
    return suggestion.submissionType === WorkflowAssistantSubmissionType.IncorrectAnswer;
  }

  /**
   * Produces a concise, traceable handoff document from queued suggestions.
   * @param suggestions Actionable queue entries to serialize.
   */
  private createMarkdown(suggestions: WorkflowAssistantSuggestion[]): string {
    const lines = ['# Workflow Assistant Feedback Queue', ''];
    suggestions.forEach(suggestion => {
      lines.push(`## #${suggestion.id}: ${suggestion.question}`);
      lines.push(`- Type: ${this.isIncorrectAnswer(suggestion) ? 'Incorrect answer report' : 'Question suggestion'}`);
      lines.push(`- Status: ${this.statuses.find(status => status.value === suggestion.status)?.label ?? suggestion.status}`);
      lines.push(`- Submitted by: ${suggestion.createdByName}`);
      if (suggestion.assistantAnswer) lines.push(`- Assistant answer: ${suggestion.assistantAnswer}`);
      lines.push(`- ${this.isIncorrectAnswer(suggestion) ? 'User feedback' : 'Expected behavior'}: ${suggestion.expectedBehavior}`);
      if (suggestion.exampleReference) lines.push(`- Example/reference: ${suggestion.exampleReference}`);
      if (suggestion.developerNotes) lines.push(`- Developer notes: ${suggestion.developerNotes}`);
      lines.push('');
    });
    return lines.join('\n');
  }
}
